import "./journals-title.scss";

import { JSX, ReactNode, useState } from "react";
import { EllipsisOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dropdown, Menu } from "antd";
import { journalsQueries } from "@/entities/journal";
import { useJournalVersionsStore } from "@/features/journal-version-list";
import { useFlags } from "@/shared/lib/flags";
import { downloadFile } from "@/shared/utils";
import type { components } from "@entities/journal";
import { ProjectRelatedTabs } from "@features/project-related-tabs";
import { useCoordinator, useNavigationPosition } from "@shared/lib/hooks";
import { routes, useNavigate } from "@shared/lib/react-router";
import { NotFoundFallback } from "@shared/ui";
import { JournalsRegistrationDetails } from "../../../../../journals-registration-details/ui";
import { JournalsAmendments } from "../amendments";
import { TitleGeneralTab } from "../general";
import { JournalsNotes } from "../notes";

type TabVariants = "general" | "notes" | "details" | "amendments";
const ALL_TABS: TabVariants[] = ["general", "notes", "details", "amendments"];
const ALL_TAB_LABELS = [
  "Общие сведения",
  "Замечания",
  "Сведения о регистрации в ГСН",
  "Заявки на изменения",
];

type TitleNotesListResponse = components["schemas"]["TitleNotesListResponse"];
type CommentsListItem = components["schemas"]["CommentsListItem"];

export const JournalsTitle = () => {
  const { titleId, projectId, objectId, mainJournalVersionId } =
    useCoordinator() as {
      titleId: string;
      projectId: string;
      objectId: string;
      mainJournalVersionId: string;
    };
  const navigate = useNavigate();
  const {
    isTitleAmendmentsForm,
    titleTailSegment,
    isTitle,
    isJournalVersionTitle,
  } = useNavigationPosition();
  const queryClient = useQueryClient();

  const isVersionsEnabled = useFlags("ojrFormationFlowEnabledV1");
  const isAmendmentsEnabled = useFlags("ojrFormationFlowEnabledV1");

  const journalVersions = useJournalVersionsStore((s) => s.versions);
  const currentJournalVersion = journalVersions.find(
    (v) => v.versionId === mainJournalVersionId,
  );
  const isViewVersionsVisible =
    isVersionsEnabled &&
    (isTitle ||
      (isJournalVersionTitle && currentJournalVersion?.status === "ACTUAL"));

  // Скрываем вкладку изменений: для неактуальных версий ИЛИ если фича выключена
  const shouldHideAmendments =
    !isAmendmentsEnabled ||
    (isJournalVersionTitle && currentJournalVersion?.status !== "ACTUAL");

  const tabVariantsArray = shouldHideAmendments
    ? ALL_TABS.filter((t) => t !== "amendments")
    : ALL_TABS;

  const tabLabels = shouldHideAmendments
    ? ALL_TAB_LABELS.filter((_, i) => ALL_TABS[i] !== "amendments")
    : ALL_TAB_LABELS;

  const currentTab: TabVariants = (() => {
    if (tabVariantsArray.includes(titleTailSegment as TabVariants)) {
      return titleTailSegment as TabVariants;
    }
    return "general";
  })();

  const {
    data: titleData,
    isLoading,
    isError,
    error,
  } = useQuery(journalsQueries.getTitleJournal({ titleId }));

  const mainJournalId = titleData?.mainJournal?.mainJournalId || "";
  const titleNotesListId =
    titleData?.mainJournal?.titleJournal?.titleNotesListId || "";

  const { data: xmlNotesList } = useQuery({
    ...journalsQueries.getTitleNotesXmlList({
      titleNotesListId,
    }),
    enabled: currentTab === "notes" && !!titleNotesListId,
  });

  const { data: detailsData } = useQuery({
    ...journalsQueries.getTitleDetails({
      mainJournalId: titleData?.mainJournal?.mainJournalId || "",
      registrationInfoId:
        titleData?.mainJournal?.titleJournal?.registrationInfoId || "",
      titleId,
    }),
    enabled:
      currentTab === "details" &&
      !!titleData?.mainJournal?.mainJournalId &&
      !!titleData?.mainJournal?.titleJournal?.registrationInfoId &&
      !!titleId,
  });

  const titleNotesQuery = journalsQueries.getTitleNotes({
    mainJournalId,
    titleNotesListId,
    titleId,
  });

  const { data: titleNotesData } = useQuery({
    ...titleNotesQuery,
    enabled:
      currentTab === "notes" &&
      !!mainJournalId &&
      !!titleNotesListId &&
      !!titleId,
  });

  const cachedTitleNotes = queryClient.getQueryData<TitleNotesListResponse>(
    titleNotesQuery.queryKey,
  );
  const notesForTabIndicator: TitleNotesListResponse | undefined =
    currentTab === "notes" ? titleNotesData : cachedTitleNotes;
  const hasActiveNotes =
    notesForTabIndicator?.commentsList?.commentsListItem?.some(
      (item: CommentsListItem) => item.isCurrent,
    ) ?? false;

  const [, setLoadingFileIds] = useState<Record<string, boolean>>({});

  const setLoading = (fileId: string, loading: boolean) => {
    setLoadingFileIds((prev) => ({ ...prev, [fileId]: loading }));
  };

  const xmlNotesListFiles = xmlNotesList?.files || [];

  const handleViewVersions = () => {
    navigate(
      routes.journals.journalVersions(
        projectId,
        objectId,
        mainJournalId,
        titleId,
      ),
    );
  };

  const menu = (
    <Menu>
      {currentTab === "notes" && (
        <Menu.SubMenu
          key="notes"
          title={
            <Menu.Item
              key="3"
              icon={(<i className="icon-xml" />) as JSX.Element}
            >
              Скачать XML
            </Menu.Item>
          }
        >
          {xmlNotesListFiles?.map((item) => {
            return (
              <Menu.Item
                key="notes-1"
                onClick={() => downloadFile(item.fileId as string, setLoading)}
              >
                {item.fileName}
              </Menu.Item>
            );
          })}
        </Menu.SubMenu>
      )}
      {currentTab === "details" && detailsData?.registrationInfo?.fileId && (
        <Menu.Item
          key="details-download"
          icon={<i className="icon-xml" />}
          onClick={() =>
            downloadFile(
              detailsData?.registrationInfo?.fileId || "",
              setLoading,
            )
          }
        >
          Скачать XML
        </Menu.Item>
      )}
      {isViewVersionsVisible && (
        <Menu.Item key="view-versions" onClick={handleViewVersions}>
          Просмотреть версии титульного листа
        </Menu.Item>
      )}
    </Menu>
  );

  const updateTab = (newTab: TabVariants) => {
    navigate(newTab);
  };

  if (isError) {
    return (
      <NotFoundFallback
        status={error?.code}
        className="base-error-page-wrapper--fill"
      />
    );
  }

  return (
    <>
      {isTitleAmendmentsForm ? null : (
        <div className="related-tabs-container">
          <ProjectRelatedTabs
            tabs={tabVariantsArray}
            tabsTranslate={tabLabels}
            activeTab={currentTab}
            setActiveTab={(tab: string) => updateTab(tab as TabVariants)}
            tabsWithDot={[false, hasActiveNotes, false]}
          />

          <Dropdown overlay={menu} trigger={["hover"]}>
            <Button
              shape="circle"
              icon={(<EllipsisOutlined />) as ReactNode}
              style={{
                borderRadius: "10px",
              }}
            />
          </Dropdown>
        </div>
      )}

      <>
        {currentTab === "general" ? (
          <TitleGeneralTab isLoading={isLoading} />
        ) : null}
        {currentTab === "notes" ? <JournalsNotes /> : null}
        {currentTab === "details" ? <JournalsRegistrationDetails /> : null}
        {currentTab === "amendments" ? <JournalsAmendments /> : null}
      </>
    </>
  );
};
